<template>
    <BaseModal
        v-if="props.showModal"
        :title="isEditing ? 'Edit Class Subject' : 'Add Class Subject'"
        :loading="loading"
        :submit-text="isEditing ? 'Update' : 'Create'"
        max-width="7xl"
        @close="$emit('close')"
        @submit="handleSubmit"
    >
        <form @submit.prevent="handleSubmit" class="space-y-6">
            <!-- Basic Information Section -->
            <div>
                <h4
                    class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                    Basic Information
                </h4>
                <div class="grid grid-cols-2 gap-4">
                    <!-- Class Selection -->
                    <div>
                        <SearchableSelect
                            v-model="form.class_id"
                            :options="classOptions"
                            label="Class"
                            placeholder="Select Class"
                            :optionLabel="
                                (classOption) =>
                                    `${classOption.grade_level} - ${classOption.name}`
                            "
                            optionValue="id"
                            :required="true"
                            :error="errors.class_id"
                        />
                    </div>

                    <!-- Subject Selection -->
                    <div>
                        <SearchableSelect
                            v-model="form.subject_id"
                            :options="subjectOptions"
                            label="Subject"
                            placeholder="Select Subject"
                            :optionLabel="
                                (subject) => `${subject.name} (${subject.code})`
                            "
                            optionValue="id"
                            :required="true"
                            :error="errors.subject_id"
                        />
                    </div>

                    <!-- Teacher Selection -->
                    <div>
                        <SearchableSelect
                            v-model="form.teacher_id"
                            :options="teacherOptions"
                            label="Teacher"
                            placeholder="Select Teacher"
                            :optionLabel="
                                (teacher: Teacher) => teacher.user?.name || ''
                            "
                            optionValue="id"
                            :required="true"
                            :error="errors.teacher_id"
                        />
                    </div>

                    <!-- Semester Selection -->
                    <div>
                        <SearchableSelect
                            v-model="form.semester_id"
                            :options="semesterOptions"
                            label="Semester"
                            placeholder="Select Semester"
                            :optionLabel="
                                (semester) =>
                                    `${semester.name} - ${semester.academic_year?.name}`
                            "
                            optionValue="id"
                            :required="true"
                            :error="errors.semester_id"
                        />
                    </div>
                </div>
            </div>

            <!-- Schedule & Room Section -->
            <div>
                <h4
                    class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                    Schedule & Room
                </h4>
                <div class="grid grid-cols-2 gap-4">
                    <!-- Schedule Input -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Schedule
                        </label>
                        <input
                            v-model="form.schedule"
                            type="text"
                            placeholder="e.g., Monday 08:00-09:30"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.schedule,
                            }"
                        />
                        <p
                            v-if="errors.schedule && errors.schedule.length > 0"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.schedule[0] }}
                        </p>
                    </div>

                    <!-- Room Input -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Room
                        </label>
                        <input
                            v-model="form.room"
                            type="text"
                            placeholder="e.g., Lab 1, Room 201"
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                            :class="{
                                'border-red-500': errors.room,
                            }"
                        />
                        <p
                            v-if="errors.room && errors.room.length > 0"
                            class="mt-1 text-sm text-red-600"
                        >
                            {{ errors.room[0] }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Settings Section -->
            <div>
                <h4
                    class="text-lg font-medium text-gray-900 dark:text-white mb-4 pb-2 border-b border-gray-200 dark:border-gray-700"
                >
                    Settings
                </h4>
                <div>
                    <div class="flex items-center">
                        <input
                            v-model="form.is_active"
                            type="checkbox"
                            id="is_active"
                            class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                            for="is_active"
                            class="ml-2 block text-sm text-gray-700 dark:text-gray-300"
                        >
                            Active
                        </label>
                    </div>
                </div>
            </div>
        </form>
    </BaseModal>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import type { ClassSubject } from "@/api/classSubject";
import { useClassSubjectStore } from "@/stores/classSubjectStore";
import { classSchoollApi, type ClassSchooll } from "@/api/classSchooll";
import { subjectApi, type Subject } from "@/api/subject";
import { teacherApi, type Teacher } from "@/api/teacher";
import semesterApi, { type Semester } from "@/api/semester";
import SearchableSelect from "@/components/SearchableSelect.vue";
import BaseModal from "@/components/BaseModal.vue";

interface Props {
    showModal: boolean;
    classSubject?: ClassSubject | null;
    isEditing: boolean;
}

interface Emits {
    (e: "close"): void;
    (e: "save"): (classSubjectData: any) => void;
    (e: "saved"): void;
}

const props = defineProps<Props>();

const emit = defineEmits<Emits>();

const classSubjectStore = useClassSubjectStore();

const form = ref({
    class_id: "",
    subject_id: "",
    teacher_id: "",
    semester_id: "",
    schedule: "",
    room: "",
    is_active: true,
});

const errors = ref<Record<string, string[]>>({});
const loading = ref(false);

const classOptions = ref<ClassSchooll[]>([]);
const subjectOptions = ref<Subject[]>([]);
const teacherOptions = ref<Teacher[]>([]);
const semesterOptions = ref<Semester[]>([]);

const fetchOptions = async () => {
    try {
        const [
            classesResponse,
            subjectsResponse,
            teachersResponse,
            semestersResponse,
        ] = await Promise.all([
            classSchoollApi.getAll({ per_page: 1000 }),
            subjectApi.getAll({ per_page: 1000 }),
            teacherApi.getAll({ per_page: 1000 }),
            semesterApi.getSemesters({ per_page: 1000 }),
        ]);
        const classData: any = classesResponse.data;
        const subjectData: any = subjectsResponse.data;
        const teacherData: any = teachersResponse.data;
        const semesterData: any = semestersResponse.data;
        classOptions.value = classData?.items || [];
        subjectOptions.value = subjectData?.items || [];
        teacherOptions.value = teacherData?.items || [];
        semesterOptions.value = semesterData?.items || [];

        if (props.isEditing && props.classSubject && props.showModal) {
            initializeForm();
        }
    } catch (error) {
        console.error("Failed to fetch options:", error);
    }
};

const validateForm = () => {
    const newErrors: Record<string, string[]> = {};

    if (!form.value.class_id.trim()) {
        newErrors.class_id = ["Class is required"];
    }

    if (!form.value.subject_id.trim()) {
        newErrors.subject_id = ["Subject is required"];
    }

    if (!form.value.teacher_id.trim()) {
        newErrors.teacher_id = ["Teacher is required"];
    }

    if (!form.value.semester_id.trim()) {
        newErrors.semester_id = ["Semester is required"];
    }

    errors.value = newErrors;
    return Object.keys(newErrors).length === 0;
};

const handleSubmit = async () => {
    if (!validateForm()) {
        return;
    }

    loading.value = true;
    errors.value = {};

    try {
        if (props.isEditing && props.classSubject) {
            await classSubjectStore.updateClassSubject(
                props.classSubject.id,
                form.value,
            );
        } else {
            await classSubjectStore.createClassSubject(form.value);
        }
        form.value = {
            class_id: "",
            subject_id: "",
            teacher_id: "",
            semester_id: "",
            schedule: "",
            room: "",
            is_active: true,
        };
        errors.value = {};
        emit("saved");
    } catch (error: any) {
        console.error("Save error:", error);

        if (error.response?.status === 422) {
            errors.value = error.response.data.errors || {};
        } else {
            errors.value = {
                general: [error.message || "Failed to save class subject"],
            };
        }
    } finally {
        loading.value = false;
    }
};

const initializeForm = () => {
    if (props.classSubject && props.isEditing) {
        form.value = {
            class_id: String(props.classSubject.class_id || ""),
            subject_id: String(props.classSubject.subject_id || ""),
            teacher_id: String(props.classSubject.teacher_id || ""),
            semester_id: String(props.classSubject.semester_id || ""),
            schedule: props.classSubject.schedule || "",
            room: props.classSubject.room || "",
            is_active:
                props.classSubject.is_active !== undefined
                    ? props.classSubject.is_active
                    : true,
        };
        console.log("Form initialized with:", form.value);
    } else if (!props.isEditing) {
        form.value = {
            class_id: "",
            subject_id: "",
            teacher_id: "",
            semester_id: "",
            schedule: "",
            room: "",
            is_active: true,
        };
    }
};

watch(
    () => [props.classSubject, props.isEditing],
    () => {
        initializeForm();
    },
    { immediate: true, deep: true },
);

watch(
    () => props.showModal,
    async (isOpen) => {
        console.log(
            "Modal visibility changed:",
            isOpen,
            "isEditing:",
            props.isEditing,
        );

        if (isOpen) {
            if (props.isEditing && props.classSubject) {
                await fetchOptions();

                form.value = {
                    class_id: String(props.classSubject.class_id || ""),
                    subject_id: String(props.classSubject.subject_id || ""),
                    teacher_id: String(props.classSubject.teacher_id || ""),
                    semester_id: String(props.classSubject.semester_id || ""),
                    schedule: props.classSubject.schedule || "",
                    room: props.classSubject.room || "",
                    is_active:
                        props.classSubject.is_active !== undefined
                            ? props.classSubject.is_active
                            : true,
                };
            } else {
                form.value = {
                    class_id: "",
                    subject_id: "",
                    teacher_id: "",
                    semester_id: "",
                    schedule: "",
                    room: "",
                    is_active: true,
                };
                errors.value = {};
            }
        }
    },
);

fetchOptions();
</script>

<style scoped>
input,
select,
textarea {
    transition:
        border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out;
}

input:focus,
select:focus,
textarea:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

button {
    transition: all 0.2s ease-in-out;
}

.border-red-500 {
    border-color: #ef4444;
}

.text-red-600 {
    color: #dc2626;
}

.dark input,
.dark select,
.dark textarea {
    background-color: #1f2937;
    border-color: #4b5563;
    color: #f9fafb;
}

.dark input:focus,
.dark select:focus,
.dark textarea:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}
</style>
